import { useState, useRef, useEffect, FC } from "react";
import { Divider, SearchBox } from "@fluentui/react-components";
import { Open16Regular } from "@fluentui/react-icons";
import useHeaderStyles from "./Header.styles";
import logo from "../../assets/images/logo.png";
import { Link, NavLink, useNavigate } from "react-router-dom";
import ROUTES from "../../constants/routes";
import Typography from "../../components/Typography";
import { customTokens } from "../../theme/theme";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setOpenSearchPage,
  setSearchText,
} from "../../store/slices/GlobalSearchSlice";

interface IHeader {
  triggerSearchApi: any;
}

const Header: FC<IHeader> = ({ triggerSearchApi }) => {
  const styles = useHeaderStyles();
  const navRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const { searchText } = useSelector((state: RootState) => state.globalSearch);

  const navItems = [
    { label: "Home", path: ROUTES.HOME },
    {
      label: "Datasets",
      path: `${ROUTES.DATASETS}${ROUTES.FTC_DATASETS}`,
      subItems: [
        {
          label: "FTC Support",
          path: `${ROUTES.DATASETS}${ROUTES.FTC_DATASETS}`,
          disabled: false,
        },
        { label: "GHG", path: "https://ghgdi.unfccc.int", disabled: false },
        { label: "NDC Progress", path: "#", disabled: true },
        { label: "Article 6", path: "#", disabled: true },
        { label: "Mitigation", path: "#", disabled: true },
        { label: "Adaptation and Resilience", path: "#", disabled: true },
        { label: "Non-Party Stakeholders", path: "#", disabled: true },
        { label: "Global Stocktake", path: "#", disabled: true },
      ],
    },
    { label: "Countries", path: ROUTES.COUNTRIES },
    // { label: "Climate Assistant", path: ROUTES.CLIMATE_ASSISTANT },
    { label: "About", path: ROUTES.ABOUT },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (label: string, hasSubItems: boolean) => {
    if (!hasSubItems) {
      setOpenDropdown(null);
      return;
    }
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchText.trim().length >= 3) {
      const payload = {
        query: searchText,
        page: 1,
        pageSize: 10,
        sort: "Page",
      };

      triggerSearchApi(payload);
    }
  };

  const handleChange = (newValue: string) => {
    dispatch(setSearchText(newValue));

    if (newValue === "") {
      dispatch(setOpenSearchPage(false));
    }
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logoSection}>
            <Link to={ROUTES.HOME}>
              <img
                src={logo}
                alt="UN logo"
                className={styles.logo}
                onClick={() => navigate(ROUTES.HOME)}
              />
            </Link>
            <Divider vertical className={styles.divider} />
            <Typography
              variant="body3Extension"
              color={customTokens.colorNeutralForegroundInverted}
            >
              Climate Data Hub
            </Typography>
          </div>

          <div className={styles.rightSection}>
            <SearchBox
              placeholder="Search"
              style={{ width: "280px" }}
              value={searchText}
              onChange={(_, data) => handleChange(data.value)}
              onKeyDown={handleKeyDown}
            />

            <Link
              to={"http://unfccc.int/"}
              className={styles.unfcccLink}
              target="_blank"
            >
              UNFCCC Main page <Open16Regular />
            </Link>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className={styles.navContainer} ref={navRef}>
        <div className={styles.navContent}>
          <div className={styles.navItems}>
            {navItems.map(({ label, path, subItems }) => (
              <div key={label} className={styles.navItemWrapper}>
                <NavLink
                  to={path}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.activeNavItem : ""}`
                  }
                  onClick={(e) => {
                    if (subItems) {
                      e.preventDefault();
                      handleNavClick(label, true);
                    } else {
                      handleNavClick(label, false);
                      dispatch(setOpenSearchPage(false));
                      dispatch(setSearchText(""));
                    }
                  }}
                >
                  {label}
                </NavLink>

                {subItems && openDropdown === label && (
                  <div className={styles.dropdownMenu}>
                    {subItems.map(
                      (
                        { label: subLabel, path: subPath, disabled },
                        index,
                        arr
                      ) => (
                        <NavLink
                          key={subLabel}
                          to={subPath}
                          onClick={() => {
                            setOpenDropdown(null);
                            dispatch(setOpenSearchPage(false));
                            dispatch(setSearchText(""));
                          }}
                          className={`${styles.dropdownItem} ${
                            disabled ? styles.disabled : ""
                          }`}
                          style={{
                            borderBottom:
                              index === arr.length - 1 ? "none" : "",
                          }}
                        >
                          {subLabel}
                        </NavLink>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
